import { useEffect, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { bounceIn } from '@shared/styles';
import { copyToClipboard } from '@shared/util/clipboard';
import { i18n } from '@shared/util/i18n';
import { openLink } from '@shared/util/link';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import { COPY_TOOLTIP_DURATION_MS } from './constants';

export type LinkType = 'url' | 'email';

interface LinkActionButtonsProps {
  value: string;
  type: LinkType;
  showCopy?: boolean;
  showOpen?: boolean;
}

export function LinkActionButtons({
  value,
  type,
  showCopy = true,
  showOpen = true,
}: LinkActionButtonsProps) {
  const [showingCopied, setShowingCopied] = useState(false);

  useEffect(() => {
    if (!showingCopied) {
      return;
    }
    const { signal, abort } = getController();
    setControlledTimeout(() => setShowingCopied(false), COPY_TOOLTIP_DURATION_MS, signal);
    return abort;
  }, [showingCopied]);

  const copyTooltip = showingCopied ? i18n('linkActions.copiedToClipboard') : getCopyTooltip(type);

  const copy = async () => {
    const success = await copyToClipboard(value);
    if (success) {
      setShowingCopied(true);
    }
  };

  const open = () => {
    if (type === 'email') {
      // A list of addresses arrives as "a, b"; mailto wants them comma-separated without spaces.
      openLink(`mailto:${value.replace(/\s+/g, '')}`);
    } else {
      window.open(value, '_blank', 'noopener,noreferrer');
    }
  };

  const shouldRender = showCopy || showOpen;
  if (!shouldRender) {
    return null;
  }

  return (
    <View row centerVertically gap={1}>
      {showCopy && (
        <Button
          variant="tertiary"
          size="small"
          icon="squares-stacked"
          iconOnly
          iconGridSize={2.5}
          onClick={copy}
          className={bounceIn}
          tooltip={copyTooltip}
        />
      )}
      {showOpen && (
        <Button
          variant="tertiary"
          size="small"
          icon="arrow-link-out"
          iconOnly
          iconGridSize={2.25}
          onClick={open}
          className={bounceIn}
          tooltip={getOpenTooltip(type, value)}
        />
      )}
    </View>
  );
}

function getCopyTooltip(type: LinkType): string {
  if (type === 'email') {
    return i18n('linkActions.copyEmailToClipboard');
  } else {
    return i18n('linkActions.copyToClipboard');
  }
}

function getOpenTooltip(type: LinkType, value: string): string {
  if (type === 'email') {
    return i18n('linkActions.openEmail', { email: value });
  } else {
    return i18n('linkActions.openUrl', { url: value });
  }
}
